// src/api/axios.ts

import axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';

import {
  AUTH_ENDPOINTS,
} from './';

import {
  store,
} from '../app/store';

import {
  clearTokens,
  setTokens,
  type TokenPayload,
} from '../features/auth/authSlice';


type RetryableRequestConfig =
  InternalAxiosRequestConfig & {
    _retry?: boolean;
  };


export type RefreshTokenResponse = {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  accessTokenExpiresIn?: number;
};


type ApiResponse<T> = {
  success?: boolean;
  code?: string;
  message?: string;
  data?: T;
};


const baseURL =
  process.env.REACT_APP_API_BASE_URL;


/**
 * Refresh Token 저장 Key
 */
const REFRESH_TOKEN_STORAGE_KEY =
  'refreshToken';


if (!baseURL) {
  // eslint-disable-next-line no-console
  console.warn(
    'REACT_APP_API_BASE_URL is not set.',
  );
}


/**
 * =========================================================
 * 일반 API Client
 * =========================================================
 */
const api =
  axios.create({
    baseURL,

    timeout:
      10000,

    headers: {
      'Content-Type':
        'application/json',
    },
  });


/**
 * =========================================================
 * Refresh Token 조회
 *
 * 우선순위:
 *
 * 1. Redux
 * 2. localStorage
 *
 * 브라우저 새로고침 시 Redux는 초기화될 수 있으므로
 * localStorage fallback이 필요합니다.
 * =========================================================
 */
const getStoredRefreshToken =
  (): string | null => {

    const reduxRefreshToken =
      store
        .getState()
        .auth
        .refreshToken;


    if (
      reduxRefreshToken
    ) {
      return reduxRefreshToken;
    }


    return localStorage.getItem(
      REFRESH_TOKEN_STORAGE_KEY,
    );
  };


/**
 * =========================================================
 * Refresh Token 저장
 * =========================================================
 */
const saveRefreshToken = (
  refreshToken: string,
): void => {

  localStorage.setItem(
    REFRESH_TOKEN_STORAGE_KEY,
    refreshToken,
  );
};


/**
 * =========================================================
 * Refresh Token 삭제
 * =========================================================
 */
const removeRefreshToken =
  (): void => {

    localStorage.removeItem(
      REFRESH_TOKEN_STORAGE_KEY,
    );
  };


/**
 * =========================================================
 * Request Interceptor
 *
 * Access Token 자동 첨부
 * =========================================================
 */
api.interceptors.request.use(
  (
    config:
      InternalAxiosRequestConfig,
  ) => {

    const {
      accessToken,
      tokenType,
    } =
      store
        .getState()
        .auth;


    if (
      accessToken
    ) {

      config.headers.Authorization =
        `${
          tokenType ||
          'Bearer'
        } ${accessToken}`;
    }


    return config;
  },


  (
    error: unknown,
  ) =>
    Promise.reject(
      error,
    ),
);


/**
 * =========================================================
 * Refresh 동시 실행 방지
 *
 * 여러 요청에서 동시에 401이 발생해도
 * 실제 Refresh 요청은 한 번만 실행합니다.
 *
 * Refresh Token Rotation을 사용하므로
 * 이 로직이 특히 중요합니다.
 * =========================================================
 */
let refreshPromise:
  Promise<
    RefreshTokenResponse | null
  > | null =
  null;


/**
 * =========================================================
 * 실제 Refresh API 호출
 *
 * 일반 api 인스턴스를 사용하지 않습니다.
 *
 * 이유:
 *
 * refresh 요청 자체가 401이 되었을 때
 * response interceptor가 다시 refresh를 요청하는
 * 무한 루프를 방지하기 위해서입니다.
 * =========================================================
 */
const requestRefreshToken =
  async (): Promise<
    RefreshTokenResponse | null
  > => {

    const refreshToken =
      getStoredRefreshToken();


    if (
      !refreshToken
    ) {
      return null;
    }


    if (
      !baseURL
    ) {

      throw new Error(
        'API Base URL이 설정되지 않았습니다.',
      );
    }


    const response =
      await axios.post<
        ApiResponse<
          RefreshTokenResponse
        >
      >(
        `${baseURL}${AUTH_ENDPOINTS.REFRESH}`,

        {
          refreshToken,
        },

        {
          timeout:
            5000,

          headers: {
            'Content-Type':
              'application/json',
          },
        },
      );


    const tokenData =
      response.data?.data;


    if (
      !tokenData?.accessToken
    ) {
      return null;
    }


    return tokenData;
  };


/**
 * =========================================================
 * 공용 Access Token 재발급
 *
 * 사용처:
 *
 * 1. 앱 시작 시 자동 로그인
 * 2. Axios 401 Interceptor
 *
 * 둘 다 같은 refreshPromise를 사용하기 때문에
 * 동시에 Refresh가 여러 번 실행되지 않습니다.
 * =========================================================
 */
export const refreshAccessToken =
  async (): Promise<
    RefreshTokenResponse | null
  > => {

    /**
     * 이미 Refresh 진행 중
     *
     * 기존 Promise를 그대로 기다립니다.
     */
    if (
      refreshPromise
    ) {
      return refreshPromise;
    }


    refreshPromise =
      (async () => {

        try {

          const tokenData =
            await requestRefreshToken();


          if (
            !tokenData?.accessToken
          ) {
            return null;
          }


          /**
           * =================================================
           * Redux 저장용 Token Payload
           * =================================================
           */
          const tokenPayload:
            TokenPayload = {

              accessToken:
                tokenData.accessToken,

              tokenType:
                tokenData.tokenType ??
                'Bearer',
            };


          /**
           * =================================================
           * Refresh Token Rotation
           *
           * 백엔드 AuthService.refresh()는
           * 새로운 Refresh Token을 발급합니다.
           *
           * 따라서 반드시 새 Refresh Token으로
           * 교체해야 합니다.
           * =================================================
           */
          if (
            tokenData.refreshToken
          ) {

            tokenPayload.refreshToken =
              tokenData.refreshToken;


            saveRefreshToken(
              tokenData.refreshToken,
            );
          }


          if (
            tokenData
              .accessTokenExpiresIn !==
            undefined
          ) {

            tokenPayload
              .accessTokenExpiresIn =
              tokenData
                .accessTokenExpiresIn;
          }


          /**
           * Redux Token 갱신
           */
          store.dispatch(
            setTokens(
              tokenPayload,
            ),
          );


          return tokenData;

        } catch (
          error
        ) {

          /**
           * Refresh 자체가 실패하면
           * 저장된 Refresh Token도 제거합니다.
           */
          removeRefreshToken();


          store.dispatch(
            clearTokens(),
          );


          throw error;

        } finally {

          refreshPromise =
            null;

        }
      })();


    return refreshPromise;
  };


/**
 * =========================================================
 * Response Interceptor
 * =========================================================
 */
api.interceptors.response.use(
  (
    response,
  ) =>
    response,


  async (
    error:
      AxiosError,
  ): Promise<unknown> => {

    const originalRequest =
      error.config as
        | RetryableRequestConfig
        | undefined;


    const status =
      error.response?.status;


    /**
     * =====================================================
     * 반드시 401만 Refresh
     *
     * 401:
     * 인증 토큰 만료 / 인증 실패
     *
     * 403:
     * 인증은 되었지만 권한 없음
     *
     * 403에서 Refresh를 하면 안 됩니다.
     * =====================================================
     */
    if (
      !originalRequest ||
      status !==
        401 ||
      originalRequest._retry
    ) {

      return Promise.reject(
        error,
      );
    }


    /**
     * Refresh Token 자체가 없다면
     * 재발급 불가
     */
    const refreshToken =
      getStoredRefreshToken();


    if (
      !refreshToken
    ) {

      store.dispatch(
        clearTokens(),
      );


      return Promise.reject(
        error,
      );
    }


    /**
     * 같은 요청을
     * 두 번 이상 재시도하지 않음
     */
    originalRequest._retry =
      true;


    try {

      const tokenData =
        await refreshAccessToken();


      if (
        !tokenData?.accessToken
      ) {

        throw new Error(
          'Access Token 재발급에 실패했습니다.',
        );
      }


      /**
       * =================================================
       * 실패했던 요청의 Authorization 갱신
       * =================================================
       */
      originalRequest
        .headers
        .Authorization =
        `${
          tokenData.tokenType ||
          'Bearer'
        } ${
          tokenData.accessToken
        }`;


      /**
       * =================================================
       * 원래 요청 재시도
       * =================================================
       */
      return api(
        originalRequest,
      );

    } catch (
      refreshError
    ) {

      /**
       * =================================================
       * Refresh 실패
       *
       * 실제 로그아웃 상태로 처리
       * =================================================
       */
      removeRefreshToken();


      store.dispatch(
        clearTokens(),
      );


      return Promise.reject(
        refreshError,
      );
    }
  },
);


export default api;